import type { MaterialProfile, PrinterProfile } from "./types";

const BASE = "/api/v1";

// Optional API key (when the backend has API_KEYS set). Stored locally.
export function getApiKey(): string {
  return localStorage.getItem("pf_api_key") || "";
}
export function setApiKey(k: string): void {
  localStorage.setItem("pf_api_key", k);
}

function headers(json = false): Record<string, string> {
  const h: Record<string, string> = {};
  if (json) h["Content-Type"] = "application/json";
  const key = getApiKey();
  if (key) h["x-api-key"] = key;
  return h;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, init);
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      detail = (await res.json()).detail ?? detail;
    } catch {
      /* ignore */
    }
    throw new Error(typeof detail === "string" ? detail : `${res.status}`);
  }
  return (await res.json()) as T;
}

const get = <T>(p: string) => req<T>(p, { headers: headers() });
const post = <T>(p: string, body?: unknown) =>
  req<T>(p, { method: "POST", headers: headers(true), body: body ? JSON.stringify(body) : undefined });
const patch = <T>(p: string, body: unknown) =>
  req<T>(p, { method: "PATCH", headers: headers(true), body: JSON.stringify(body) });
const del = <T>(p: string) => req<T>(p, { method: "DELETE", headers: headers() });

// -- Types --
export interface Health {
  status: string;
  service: string;
  version: string;
  env: string;
}
export interface GeneratorInfo {
  name: string;
  title: string;
  description: string;
  params_schema: { properties?: Record<string, { type?: string; default?: unknown }> };
}
export interface TemplateInfo {
  generator: string;
  printer_profile: string;
  build_volume_mm: [number, number, number];
  max_dims_mm: { x: number; y: number; z: number };
  default_layer_height_mm: number;
  compatible_materials: string[];
  params_schema: GeneratorInfo["params_schema"];
}
export interface BuildValidation {
  manifold: boolean;
  watertight: boolean;
  fits_build_volume: boolean;
  min_wall_mm: number;
  overhang_faces: number;
  score: number;
  passed: boolean;
}
export interface BuildResponse {
  generator: string;
  mesh_ref: string;
  mesh_url: string;
  passed: boolean;
  score: number;
  checks: { name: string; status: string; detail: string; measured?: number | null; threshold?: number | null }[];
  critique: string[];
  validation: BuildValidation;
}
export interface Project {
  id: string;
  name: string;
  description: string | null;
  group: string | null;
  created_at: string;
  shared: boolean;
  // share_token is not exposed in list/detail (secret capability); only POST /share returns it.
  file_count?: number;
  ok_count?: number;
  // Lightweight preview hints for list thumbnails (representative file).
  preview_material?: string | null;
  preview_passed?: boolean | null;
  preview_file_id?: string | null;
  files?: ProjectFile[];
}
export interface ProjectFile {
  id: string;
  project_id: string;
  name: string;
  kind: string;
  generator: string | null;
  printer_profile: string;
  material: string;
  passed: boolean;
  score: number;
  has_mesh?: boolean;
  has_thumb?: boolean;
  created_at: string;
}

// -- Endpoints --
export const fetchHealth = () => get<Health>("/health");
export const fetchPrinters = () => get<PrinterProfile[]>("/profiles/printers");
export const fetchMaterials = () => get<MaterialProfile[]>("/profiles/materials");
export const fetchGenerators = () => get<GeneratorInfo[]>("/generators");
export const fetchTemplate = (name: string, printer: string) =>
  get<TemplateInfo>(`/generators/${name}/template?printer_profile=${encodeURIComponent(printer)}`);
export const buildGenerator = (
  name: string,
  body: { printer_profile: string; material: string; params: Record<string, unknown> },
) => post<BuildResponse>(`/generators/${name}/build`, body);

export const listProjects = () => get<Project[]>("/projects");
export const listGroups = () => get<string[]>("/projects/groups");
export const createProject = (name: string, description?: string, group?: string) =>
  post<Project>("/projects", { name, description, group });
export const getProject = (id: string) => get<Project>(`/projects/${id}`);
export const updateProject = (
  id: string,
  body: { name?: string; description?: string; group?: string | null; set_group?: boolean },
) => patch<Project>(`/projects/${id}`, body);
export const deleteProject = (id: string) => del<{ status: string }>(`/projects/${id}`);
export const duplicateProject = (id: string) => post<Project>(`/projects/${id}/duplicate`);
export const shareProject = (id: string) =>
  post<{ share_token: string; share_url: string }>(`/projects/${id}/share`);

export const listFiles = (pid: string) => get<ProjectFile[]>(`/projects/${pid}/files`);
export const addFile = (pid: string, body: Record<string, unknown>) =>
  post<ProjectFile>(`/projects/${pid}/files`, body);
export const renameFile = (pid: string, fid: string, name: string) =>
  patch<ProjectFile>(`/projects/${pid}/files/${fid}`, { name });
export const deleteFile = (pid: string, fid: string) =>
  del<{ status: string }>(`/projects/${pid}/files/${fid}`);
export const duplicateFile = (pid: string, fid: string) =>
  post<ProjectFile>(`/projects/${pid}/files/${fid}/duplicate`);
export const generateFile = (
  pid: string,
  body: { generator: string; params: Record<string, unknown>; printer_profile: string; material: string; name?: string },
) => post<ProjectFile & { critique: string[] }>(`/projects/${pid}/generate`, body);
export const createFromIdea = (
  pid: string,
  body: { idea: string; mode: string; printer_profile: string; material: string; name?: string },
) => post<ProjectFile & { passed: boolean; critique: string[] }>(`/projects/${pid}/create`, body);
export interface FileDetail extends ProjectFile {
  validation: BuildValidation & Record<string, unknown>;
  print_model: Record<string, unknown>;
}
export const getFile = (pid: string, fid: string) =>
  get<FileDetail>(`/projects/${pid}/files/${fid}`);

export interface RecentFile extends ProjectFile {
  project_name: string;
}
export const recentFiles = (limit = 8) => get<RecentFile[]>(`/projects/recent-files?limit=${limit}`);

export interface SliceEstimate {
  authoritative: boolean;
  print_time_s: number;
  filament_g: number;
  quote: { material_cost?: number; total?: number; currency?: string; [k: string]: unknown };
  gcode_ref: string | null;
  note: string;
}
export const sliceFile = (pid: string, fid: string) =>
  post<SliceEstimate>(`/projects/${pid}/files/${fid}/slice`);

export const exportFileUrl = (pid: string, fid: string, fmt: string) =>
  `${BASE}/projects/${pid}/files/${fid}/export?format=${fmt}`;
export const thumbnailUrl = (pid: string, fid: string) =>
  `${BASE}/projects/${pid}/files/${fid}/thumbnail`;
// Rendered preview of the WHOLE project (all parts laid out together).
export const projectPreviewUrl = (pid: string) => `${BASE}/projects/${pid}/preview`;
// Printable assembly/print notice (PDF) for the whole project.
export const projectNoticeUrl = (pid: string) => `${BASE}/projects/${pid}/notice.pdf`;
// Complete publish bundle (ZIP: STLs + render + notice + listing).
export const projectExportUrl = (pid: string) => `${BASE}/projects/${pid}/export.zip`;

export interface PrintOrderItem {
  id: string;
  name: string;
  passed: boolean;
  volume_mm3: number;
  order: number;
}
export const printOrder = (pid: string) => get<PrintOrderItem[]>(`/projects/${pid}/print-order`);
export const routeFile = (
  pid: string,
  fid: string,
  printer_name: string,
  opts: { quantity?: number; scheduled_at?: string | null } = {},
) =>
  post<{ job_id: string; job_ids: string[]; quantity: number; state: string; printer_name: string; filename: string }>(
    `/projects/${pid}/files/${fid}/route`,
    { printer_name, quantity: opts.quantity ?? 1, scheduled_at: opts.scheduled_at ?? null },
  );
export const fetchShared = (token: string) => get<Project>(`/shared/${token}`);

export interface Integration {
  id: string;
  name: string;
  category: "gateway" | "llm" | "image" | "threed" | "slicer" | "printer" | "storage" | "payment" | "observability";
  summary: string;
  configured: boolean;
  status: string;
  template: { key: string; label: string; secret: boolean; placeholder: string }[];
  docs: string;
}
export interface Integrations {
  summary: { total: number; configured: number; by_category: Record<string, number> };
  items: Integration[];
}
export const fetchIntegrations = () => get<Integrations>("/integrations");

export interface PrintJob {
  id: string;
  printer_name: string;
  filename: string;
  state: string;
  print_model_id: string | null;
  created_at?: string;
  has_gcode?: boolean;
  dispatched_to?: string | null;
  // Live-monitoring meta (progress 0..1, ETA text, timing, material, free-form note).
  progress?: number | null;
  eta?: string | null;
  material?: string | null;
  print_time_s?: number | null;
  filament_used_g?: number | null;
  cost?: number | null;
  spool_id?: string | null;
  advisory?: boolean | null;
  scheduled_at?: string | null;
  copy?: string | null;
  note?: string | null;
}
export const listJobs = () => get<PrintJob[]>("/jobs");
export const getJob = (id: string) => get<PrintJob>(`/jobs/${id}`);
export const updateJob = (
  id: string,
  body: { state?: string; progress?: number; eta?: string; note?: string },
) => patch<PrintJob>(`/jobs/${id}`, body);
// Upload the G-code to a registered printer and START the print. Outward action: confirm.
export const dispatchJob = (id: string, body: { farm_printer_id: string; confirm: boolean }) =>
  post<PrintJob>(`/jobs/${id}/dispatch`, body);
// Pause / resume / cancel the print on the job's dispatched printer (cancel needs confirm).
export const controlJob = (id: string, body: { action: "pause" | "resume" | "cancel"; confirm?: boolean }) =>
  post<PrintJob>(`/jobs/${id}/control`, body);

export interface JobStats {
  total: number;
  by_state: Record<string, number>;
  success_rate: number | null;
  total_filament_g: number;
  total_time_s: number;
  total_cost: number;
}
export const jobsStats = () => get<JobStats>("/jobs/stats");

// -- Filament spool inventory --
export interface Spool {
  id: string;
  material: string;
  name: string | null;
  vendor: string | null;
  color: string;
  total_g: number;
  remaining_g: number;
  remaining_pct: number;
  cost_per_kg: number;
  currency: string;
  low_stock: boolean;
}
export const listSpools = () => get<Spool[]>("/spools");
export const createSpool = (body: Record<string, unknown>) => post<Spool>("/spools", body);
export const updateSpool = (id: string, body: Record<string, unknown>) => patch<Spool>(`/spools/${id}`, body);
export const deleteSpool = (id: string) => del<{ status: string }>(`/spools/${id}`);

// -- Print farm (registered network printers) --
export interface FarmPrinter {
  id: string;
  name: string;
  kind: "moonraker" | "octoprint" | "prusalink";
  base_url: string;
  has_api_key: boolean;
  created_at?: string;
}
export interface FarmStatus {
  id: string;
  name: string;
  kind: string;
  online: boolean | null;
  state?: string;
  progress?: number | null;
  filename?: string | null;
  message?: string | null;
  error?: string;
}
export const listFarmPrinters = () => get<FarmPrinter[]>("/farm-printers");
export const createFarmPrinter = (body: { name: string; kind: string; base_url: string; api_key?: string }) =>
  post<FarmPrinter>("/farm-printers", body);
export const deleteFarmPrinter = (id: string) => del<{ status: string }>(`/farm-printers/${id}`);
export const farmPrinterStatus = (id: string) => get<FarmStatus>(`/farm-printers/${id}/status`);

export interface IntegrationTest {
  id: string;
  configured: boolean;
  reachable: boolean | null;
  detail: string;
}
export const testIntegration = (id: string) => post<IntegrationTest>(`/integrations/${id}/test`);

// -- Trending ideas (Ollama-first, curated fallback) --
export interface TrendIdea {
  title: string;
  prompt: string;
  mode: "parametric" | "generative";
  category: string;
  why: string;
  tags: string[];
  generator: string | null;
}
export interface TrendResponse {
  source: "llm" | "fallback";
  theme: string | null;
  ideas: TrendIdea[];
}
export const fetchTrends = (n = 8, theme?: string) =>
  get<TrendResponse>(`/trends?n=${n}${theme ? `&theme=${encodeURIComponent(theme)}` : ""}`);

// -- Marketplace publishing --
export interface PublishTarget {
  id: string;
  name: string;
  paid: boolean;
  free: boolean;
  account_required: boolean;
  upload_mode: "auto" | "prepare" | "none";
  formats: string[];
  max_file_mb: number;
  requires_render: boolean;
  required_fields: string[];
  automation_note: string;
  docs: string;
  env_keys: string[];
}
export interface PublishTargets {
  summary: { total: number; auto: number; prepare: number };
  targets: PublishTarget[];
}
export const fetchPublishTargets = () => get<PublishTargets>("/publish/targets");

export interface PreparedFile {
  name: string;
  ext: string;
  size_bytes: number;
  ok: boolean;
  reason: string;
}
export interface PublishSubmission {
  target_id: string;
  target_name: string;
  upload_mode: "auto" | "prepare" | "none";
  ready: boolean;
  listing: Record<string, unknown>;
  files: PreparedFile[];
  blockers: string[];
  warnings: string[];
  credentials_present: boolean;
  missing_env: string[];
}
export const preparePublish = (pid: string, target: string) =>
  post<PublishSubmission>(`/projects/${pid}/publish/${target}/prepare`);

export interface PublishResult {
  target: string;
  url?: string | null;
  object_id?: number;
  thing_id?: number;
  files?: number;
  [k: string]: unknown;
}
// Live upload to an auto-capable marketplace. Outward action: confirm + site creds required.
export const publishUpload = (pid: string, target: string, confirm = true) =>
  post<PublishResult>(`/projects/${pid}/publish/${target}/upload`, { confirm });

export interface Credits {
  configured: boolean;
  balance?: number | string;
  currency?: string;
  error?: string;
  [k: string]: unknown;
}
export const fetchCredits = () => get<Credits>("/credits");
